import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, Row, Spinner, Stack, Table } from 'react-bootstrap';
import { Download, Search } from 'react-bootstrap-icons';
import { useTicketOutcomesQuery } from '../../queries/ticketOutcomeReport.query';
import { useExportTicketOutcomesMutation } from '../../mutations/ticketOutcomeReport.mutation';
import { TicketOutcomeReportResponse } from '../../types/ticketOutcomeReport.types';

function TicketOutcomesReport() {
  const getDefaultDates = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  const [reportData, setReportData] = useState<TicketOutcomeReportResponse | null>(null);
  const [outcomeFilter, setOutcomeFilter] = useState<'all' | 'Dismissed' | 'Guilty'>('all');
  const [paidFilter, setPaidFilter] = useState<'all' | 'paid' | 'unpaid'>('all');

  const { mutate: fetchOutcomes, isLoading: isFetching } = useTicketOutcomesQuery();
  const { mutate: exportOutcomes, isLoading: isExporting } = useExportTicketOutcomesMutation();

  useEffect(() => {
    fetchOutcomes(
      { startDate: defaultDates.start, endDate: defaultDates.end },
      {
        onSuccess: (data) => {
          setReportData(data);
        }
      }
    );
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) return;

    fetchOutcomes(
      { startDate, endDate },
      {
        onSuccess: (data) => {
          setReportData(data);
        }
      }
    );
  };

  const handleExport = () => {
    if (!startDate || !endDate) return;
    exportOutcomes({ startDate, endDate });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredOutcomes = reportData?.outcomes.filter((outcome) => {
    if (outcomeFilter !== 'all' && outcome.outcome !== outcomeFilter) return false;
    if (paidFilter === 'paid' && outcome.paidStatus === 'Unpaid') return false;
    if (paidFilter === 'unpaid' && outcome.paidStatus !== 'Unpaid') return false;
    return true;
  });

  return (
    <Card className="mt-4">
      <Card.Header>
        <h5 className="mb-0">Ticket Outcomes Report</h5>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSearch}>
          <Row className="align-items-end mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Button type="submit" disabled={isFetching || !startDate || !endDate}>
                {isFetching ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  <>
                    <Search className="me-1" /> Search
                  </>
                )}
              </Button>
            </Col>
            {reportData && reportData.outcomes.length > 0 && (
              <Col md={3} className="text-end">
                <Button
                  variant="outline-primary"
                  onClick={handleExport}
                  disabled={isExporting || !startDate || !endDate}>
                  {isExporting ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <>
                      <Download className="me-1" /> Export CSV
                    </>
                  )}
                </Button>
              </Col>
            )}
          </Row>
        </Form>

        {reportData && (
          <>
            <Stack direction="horizontal" gap={3} className="mb-3 flex-wrap">
              <Badge bg="secondary" pill>
                Total: {reportData.totalCount}
              </Badge>
              <Badge bg="success" pill>
                Dismissed: {reportData.dismissedCount}
              </Badge>
              <Badge bg="danger" pill>
                Guilty: {reportData.guiltyCount}
              </Badge>
              <Badge bg="primary" pill>
                Paid: {reportData.paidCount}
              </Badge>
              <Badge bg="warning" text="dark" pill>
                Unpaid: {reportData.unpaidCount}
              </Badge>
            </Stack>

            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Outcome</Form.Label>
                  <Form.Select
                    value={outcomeFilter}
                    onChange={(e) =>
                      setOutcomeFilter(e.target.value as 'all' | 'Dismissed' | 'Guilty')
                    }>
                    <option value="all">All</option>
                    <option value="Dismissed">Dismissed</option>
                    <option value="Guilty">Guilty</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Paid Status</Form.Label>
                  <Form.Select
                    value={paidFilter}
                    onChange={(e) => setPaidFilter(e.target.value as 'all' | 'paid' | 'unpaid')}>
                    <option value="all">All</option>
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6} className="d-flex align-items-end">
                <span className="text-muted">
                  Showing {filteredOutcomes?.length ?? 0} of {reportData.totalCount} results
                </span>
              </Col>
            </Row>

            {filteredOutcomes && filteredOutcomes.length > 0 ? (
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Summons Number</th>
                    <th>Outcome</th>
                    <th>Outcome Date</th>
                    <th>Paid Status</th>
                    <th>Client Name</th>
                    <th>Client Email</th>
                    <th>Plate Number</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOutcomes.map((outcome) => (
                    <tr key={outcome.ticketId}>
                      <td>{outcome.summonsNumber}</td>
                      <td>
                        <Badge bg={outcome.outcome === 'Dismissed' ? 'success' : 'danger'}>
                          {outcome.outcome}
                        </Badge>
                      </td>
                      <td>{formatDate(outcome.outcomeDate)}</td>
                      <td>
                        <Badge
                          bg={
                            outcome.paidStatus === 'Unpaid'
                              ? 'warning'
                              : outcome.paidStatus === 'Paid via Charge'
                              ? 'primary'
                              : 'info'
                          }
                          text={outcome.paidStatus === 'Unpaid' ? 'dark' : undefined}>
                          {outcome.paidStatus}
                        </Badge>
                      </td>
                      <td>{outcome.clientName}</td>
                      <td>{outcome.clientEmail}</td>
                      <td>{outcome.plateNumber}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className="text-muted text-center">
                {reportData.outcomes.length === 0
                  ? 'No ticket outcomes found for the selected date range.'
                  : 'No results match the selected filters.'}
              </p>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default TicketOutcomesReport;
